import {
  HasTimelineItemModel,
  TimelineLabelWithRenderingParameters,
  TimelinePhasesWithRenderingParameters,
  TimelineViewRenderingData,
  TimelineViewModelData,
  ItemTops,
} from '../types';
import { keyBy } from 'lodash';
import { WILDCARD } from '@ardoq/common-helpers';
import { getItemRowTotalHeight } from './calculations';
import { MARGIN_BETWEEN_ROWS } from '../items/consts';

type ItemWithCollapsibleFieldData = HasTimelineItemModel & {
  isFieldDataCollapsed: boolean;
};

const collapseExpandFieldData = <T extends ItemWithCollapsibleFieldData>(
  itemData: T,
  collapsedFieldDataIds: Set<string>
): T => {
  const isFieldDataCollapsed =
    !itemData.itemModel.isFieldDataCollapsible ||
    collapsedFieldDataIds.has(WILDCARD) ||
    collapsedFieldDataIds.has(itemData.itemModel.rawItemIdPath);

  if (isFieldDataCollapsed === itemData.isFieldDataCollapsed) {
    return itemData;
  }

  return {
    ...itemData,
    isFieldDataCollapsed,
  };
};

type ApplyCollapsedFieldDataStatesArgs = {
  timelineViewRenderingData: Pick<
    TimelineViewRenderingData,
    | 'itemLabelsWithRenderingDataWithoutCollapsedHierarchy'
    | 'itemPhasesWithRenderingDataWithoutCollapsedHierarchy'
    | 'collapsedFieldDataIds'
  >;
  timelineViewModelData: Pick<TimelineViewModelData, 'collapsibleGroups'>;
};

type ReduceLabelsToItemTopsState = {
  allPhasesWithRenderingData: TimelinePhasesWithRenderingParameters[];
  allItemTopsWithSentinel: ItemTops;
  cumulativeHeight: number;
};

const reduceLabelsToItemTops = (
  {
    allItemTopsWithSentinel,
    cumulativeHeight,
    allPhasesWithRenderingData,
  }: ReduceLabelsToItemTopsState,
  labelsData: TimelineLabelWithRenderingParameters,
  index: number,
  allItems: TimelineLabelWithRenderingParameters[]
) => {
  const tops = [cumulativeHeight];

  const nextTop =
    cumulativeHeight +
    getItemRowTotalHeight(labelsData, allPhasesWithRenderingData[index]);

  if (index === allItems.length - 1) {
    // sentinel, see ItemTops type
    tops.push(nextTop);
  }

  return {
    cumulativeHeight: nextTop,
    allItemTopsWithSentinel: allItemTopsWithSentinel.concat(tops),
    allPhasesWithRenderingData,
  };
};

export const applyCollapsedFieldDataStates = ({
  timelineViewRenderingData: {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
    collapsedFieldDataIds,
  },
}: ApplyCollapsedFieldDataStatesArgs) => {
  const itemLabelsWithCollapsedFieldDataState =
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy.map(labelData =>
      collapseExpandFieldData<TimelineLabelWithRenderingParameters>(
        labelData,
        collapsedFieldDataIds
      )
    );

  const itemPhasesWithCollapsedFieldDataState =
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy.map(phasesData =>
      collapseExpandFieldData(phasesData, collapsedFieldDataIds)
    );

  // calculate the height of each row based on the max between phases and labels

  const { allItemTopsWithSentinel } =
    itemLabelsWithCollapsedFieldDataState.reduce(reduceLabelsToItemTops, {
      cumulativeHeight: MARGIN_BETWEEN_ROWS,
      allItemTopsWithSentinel: [] as number[],
      allPhasesWithRenderingData: itemPhasesWithCollapsedFieldDataState,
    });

  const renderedPhasesByGraphNodeId = keyBy(
    itemPhasesWithCollapsedFieldDataState,
    (phasesData: TimelinePhasesWithRenderingParameters) =>
      phasesData.itemModel.id
  );

  return {
    itemLabelsWithCollapsedFieldDataState,
    itemPhasesWithCollapsedFieldDataState,
    allItemTopsWithSentinel,
    renderedPhasesByGraphNodeId,
  };
};
